import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import { PermissionButton } from "@/components/ui/PermissionButton";
import CountBox from "@/components/ui/CountBox";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import Table, { TableColumn } from "@/components/ui/Table";
import AddModal from "@/components/modal/standard/driverCode/Add";
import UpdateModal from "@/components/modal/standard/driverCode/Update";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useDriverCode, useDriverCodeDelete } from "@/hooks/standard/st_driver_code/useDriverCode";
import { DriverCode } from "@/interface/standard/st_driver_code";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";

export default function DriverCodePage() {
  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState("");
  const [mobileTelValue, setMobileTelValue] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("Y"); // 재직상태 필터 (기본값: 재직)
  const [actualEmploymentStatus, setActualEmploymentStatus] = useState("Y"); // 실제 검색에 사용할 재직상태
  const [exactMatch, setExactMatch] = useState(false); // 정확히 일치 체크박스
  const [actualExactMatch, setActualExactMatch] = useState(false); // 실제 검색에 사용할 정확히 일치
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [actualSearchValue, setActualSearchValue] = useState(""); // 실제 검색에 사용할 값
  const [actualMobileTelValue, setActualMobileTelValue] = useState(""); // 실제 검색에 사용할 전화번호
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [selectedDriverCode, setSelectedDriverCode] = useState<DriverCode | undefined>(undefined);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allData, setAllData] = useState<DriverCode[]>([]);
  const [tableVer, setTableVer] = useState(0);
  const deleteDriverCode = useDriverCodeDelete();

  // 재직상태 옵션
  const employmentStatusOptions = [
    { value: "", label: "전체" },
    { value: "Y", label: "재직" },
    { value: "N", label: "퇴사" },
  ];

  // 페이지네이션을 지원하는 useDriverCode 호출
  const {
    data: driverCodeData,
    isLoading,
    error,
  } = useDriverCode({
    page: currentPage,
    size: 20,
    search: actualSearchValue,
    mobileTel: actualMobileTelValue,
    employmentStatus: actualEmploymentStatus,
    exactMatch: actualExactMatch,
    // sort는 백엔드에서 만료 기사 우선 정렬로 처리
  });

  // 검색어 또는 재직상태가 변경될 때 페이지 초기화만 수행 (데이터는 유지)
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
  }, [actualSearchValue, actualMobileTelValue, actualEmploymentStatus, actualExactMatch]);

  // 새로운 페이지 데이터가 로드되면 누적 데이터에 추가
  useEffect(() => {
    if (driverCodeData && driverCodeData.content && driverCodeData.content.length > 0) {
      // 첫 번째 페이지(currentPage === 0)이거나 검색이 변경된 경우 기존 데이터 교체
      if (currentPage === 0) {
        setAllData(driverCodeData.content);
        setTotalElements(driverCodeData.totalElements || 0);
      } else {
        // 추가 페이지는 기존 데이터에 추가
        setAllData(prev => [...prev, ...driverCodeData.content]);
      }
    } else if (driverCodeData && (!driverCodeData.content || driverCodeData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
      setTotalElements(0);
    }
  }, [driverCodeData, currentPage]);

  // hasMore 상태를 useMemo로 계산
  const hasMore = useMemo(() => {
    if (!driverCodeData) return false;
    return !driverCodeData.last && allData.length < totalElements;
  }, [driverCodeData?.last, allData.length, totalElements]);

  // 백엔드에서 이미 만료 기사 우선 정렬하므로 allData 그대로 사용

  // Table의 onLoadMore 콜백
  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }

      // 현재 표시된 데이터가 누적된 데이터의 80% 이상일 때 다음 페이지 요청
      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  // 선택된 행 데이터 관리
  useEffect(() => {
    if (selectedRows.length === 1) {
      const idx = allData[selectedRows[0]];
      setSelectedDriverCode(idx);
    } else {
      setSelectedDriverCode(undefined);
    }
  }, [selectedRows, allData]);

  // 로딩 상태 업데이트
  useEffect(() => {
    if (driverCodeData) {
      setLoadingMore(false);
    }
  }, [driverCodeData]);

  // 테이블 컬럼 정의
  const columns: TableColumn[] = [
    { key: "stDriverName", header: "기사명" },
    { key: "stDriverMobileTel", header: "전화번호" },
    {
      key: "stDriverEmploymentStatus",
      header: "재직상태",
      render: value => {
        if (value === "Y") return "재직";
        if (value === "N") return "퇴사";
        return "재직"; // 기본값
      },
    },
    {
      key: "stDriverLicenseStartDate",
      header: "면허시작일",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    {
      key: "stDriverLicenseEndDate",
      header: "면허종료일",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    {
      key: "stDriverHazmatEduStartDate",
      header: "유해물질 교육일",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    {
      key: "stDriverHazmatEduEndDate",
      header: "유해물질 교육종료일",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    {
      key: "stDriverDangerEduStartDate",
      header: "위험물 교육일",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    {
      key: "stDriverDangerEduEndDate",
      header: "위험물 교육종료일",
      render: value => {
        if (!value) return "";
        return value.split("T")[0];
      },
    },
    { key: "stDriverRemark", header: "비고" },
  ];

  const handleReset = () => {
    // 초기 상태로 리셋 (재직상태 기본값: 재직)
    setSearchValue("");
    setMobileTelValue("");
    setEmploymentStatus("Y");
    setExactMatch(false);
    setActualSearchValue("");
    setActualMobileTelValue("");
    setActualEmploymentStatus("Y");
    setActualExactMatch(false);
    setTableVer(v => v + 1);
  };

  const handleSearch = useCallback(async () => {
    setActualSearchValue(searchValue);
    setActualMobileTelValue(mobileTelValue);
    setActualEmploymentStatus(employmentStatus);
    setActualExactMatch(exactMatch);
    setCurrentPage(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
    // 캐시를 제거 후 다시 fetch하여 같은 조건 재검색에서도 데이터가 갱신되도록 함
    await queryClient.resetQueries({ queryKey: ["driverCode"] });
  }, [searchValue, mobileTelValue, employmentStatus, exactMatch, queryClient]);

  const handleAddClick = () => {
    setSelectedRows([]);
    setSelectedDriverCode(undefined);
    setIsAddModalOpen(true);
  };

  const handleUpdateClick = () => {
    // 수정 로직 구현 예정
    if (selectedRows.length === 0) {
      toast.error("수정할 기사를 선택해주세요.");
      return;
    }
    if (selectedRows.length > 1) {
      toast.error("하나의 기사만 선택해주세요.");
      return;
    }
    setSelectedDriverCode(allData[selectedRows[0]]);
    setIsUpdateModalOpen(true);
    setTableVer(v => v + 1);
  };

  const handleDeleteClick = async () => {
    if (selectedRows.length === 0) {
      toast.error("삭제할 기사를 선택해주세요.");
      return;
    }
    if (!window.confirm(`선택한 ${selectedRows.length}개의 기사를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      for (const index of selectedRows) {
        const item = allData[index];
        if (item?.stDriverCode) {
          await deleteDriverCode.mutateAsync(item.stDriverCode);
        }
      }

      // 데이터 새로고침
      handleUpdate();
    } catch (error) {
      console.error("삭제 실패:", error);
    }
  };

  const handleUpdate = (): void => {
    setAllData([]);
    setTotalElements(0);
    setCurrentPage(0);
    setSelectedRows([]);
    setSelectedDriverCode(undefined);
    setTableVer(v => v + 1);
  };

  return (
    <div className="flex flex-col h-full">
      <SearchBoxLayout>
        <div className="flex gap-3 items-end">
          <SelectBox
            label="재직상태"
            value={employmentStatus}
            onChange={value => {
              setEmploymentStatus(value);
              setActualEmploymentStatus(value);
              setAllData([]);
              setCurrentPage(0);
              setTotalElements(0);
              setLoadingMore(false);
              setTableVer(v => v + 1);
            }}
            options={employmentStatusOptions}
            className="w-32"
          />
          <InputBox
            label="기사명"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <InputBox
            label="전화번호"
            value={mobileTelValue}
            onChange={e => setMobileTelValue(e.target.value)}
            className="w-50"
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSearch();
              }
            }}
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
            <Button variant="search" onClick={handleSearch}>
              조회
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={totalElements} />
          <div className="flex gap-2">
            <PermissionButton permission="canCreate" variant="etc" onClick={handleAddClick}>
              추가
            </PermissionButton>
            <PermissionButton permission="canUpdate" variant="etc" onClick={handleUpdateClick}>
              수정
            </PermissionButton>
            <PermissionButton permission="canDelete" variant="etc" onClick={handleDeleteClick}>
              삭제
            </PermissionButton>
            <PrintButton data={allData} columns={columns} title="기사코드" />
            <ExcelButton data={allData} columns={columns} fileName="기사코드" />
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <Table
            key={`driver-table-v${tableVer}`}
            className="h-full"
            columns={columns}
            data={allData}
            showCheckbox={true}
            showRowNumber={true}
            selectedRows={selectedRows}
            onRowSelect={setSelectedRows}
            maxHeight="100%"
            infiniteScroll={true}
            initialLoadCount={20}
            getRowBgClassName={(row: DriverCode) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const tomorrow = new Date(today);
              tomorrow.setDate(tomorrow.getDate() + 1);

              const checkExpired = (dateStr: string | Date | null | undefined) => {
                if (!dateStr) return { isExpired: false, isExpiring: false };
                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return { isExpired: false, isExpiring: false };
                date.setHours(0, 0, 0, 0);
                return {
                  isExpired: date < today,
                  isExpiring: date >= today && date <= tomorrow,
                };
              };

              const license = checkExpired(row.stDriverLicenseEndDate);
              const hazmat = checkExpired(row.stDriverHazmatEduEndDate);
              const danger = checkExpired(row.stDriverDangerEduEndDate);

              // 만료된 항목이 있으면 빨간 배경
              if (license.isExpired || hazmat.isExpired || danger.isExpired) {
                return "bg-red-100";
              }
              // 만료 예정(오늘~내일)이면 노란 배경
              if (license.isExpiring || hazmat.isExpiring || danger.isExpiring) {
                return "bg-yellow-100";
              }
              return null;
            }}
            loadMoreCount={20}
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
          />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
      </main>
      <AddModal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} onSave={handleUpdate} />
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={() => {
          setIsUpdateModalOpen(false);
          setSelectedDriverCode(undefined); // 모달이 닫힐 때 선택된 데이터 초기화
          setSelectedRows([]);
        }}
        onUpdate={handleUpdate}
        driverData={selectedDriverCode}
      />
    </div>
  );
}
